"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import type { SVGProps } from "react";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useUI } from "@/lib/context/UIProvider";
import { cn } from "@/lib/utils";

// ── Dock glyphs ──────────────────────────────────────────────────
const iconBase = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
const sizeCls = "max-md:h-[22px] max-md:w-[22px] md:h-[19px] md:w-[19px]";

const FolderIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...iconBase} className={sizeCls} {...p}>
    <path d="M3 8c0-1.1 0.9-2 2-2h4l2 2h8c1.1 0 2 0.9 2 2v7c0 1.1-0.9 2-2 2H5c-1.1 0-2-0.9-2-2V8z" />
  </svg>
);
const GridIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...iconBase} className={sizeCls} {...p}>
    <rect x="4" y="4" width="7" height="7" rx="2" />
    <rect x="13" y="4" width="7" height="7" rx="2" />
    <rect x="4" y="13" width="7" height="7" rx="2" />
    <rect x="13" y="13" width="7" height="7" rx="2" />
  </svg>
);
const AwardIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...iconBase} className={sizeCls} {...p}>
    <circle cx="12" cy="10" r="5" />
    <path d="M9 14.5L8 21l4-2 4 2-1-6.5" />
  </svg>
);

/**
 * Frosted floating nav dock. Self-contained and reusable: it reads the
 * active route from the router and its labels from the locale.
 *
 * @param hideOnMobile hide the dock at small widths (e.g. when a screen
 *   shows its own bottom action bar).
 */
export function FloatingDock({ hideOnMobile = false }: { hideOnMobile?: boolean }) {
  const pathname = usePathname();
  const { t } = useLocale();
  const { sidebarOpen } = useUI();

  // Hide the dock while a full-screen modal route is open.
  const isModalRoute =
    pathname === "/projects/new" || /^\/projects\/[^/]+\/edit$/.test(pathname);

  const items = [
    { href: "/projects", label: t.navProjects, Icon: FolderIcon },
    { href: "/tools", label: t.navTools, Icon: GridIcon },
    { href: "/certificates", label: t.navCerts, Icon: AwardIcon },
  ];

  return (
    <nav
      className={cn(
        "glass-dock fixed left-1/2 z-[60] flex -translate-x-1/2 gap-1 border border-accent/15 transition-opacity duration-300",
        "max-md:bottom-[calc(12px_+_env(safe-area-inset-bottom))] max-md:w-[calc(100%_-_28px)] max-md:max-w-[430px] max-md:rounded-[30px] max-md:p-2",
        "md:bottom-[22px] md:rounded-[20px] md:p-1.5",
        sidebarOpen && "pointer-events-none opacity-0",
        isModalRoute && "pointer-events-none opacity-0",
        hideOnMobile && "max-md:hidden",
      )}
    >
      {items.map(({ href, label, Icon }) => {
          const active = pathname === href || pathname.startsWith(href + "/");
          return (
            <Link
              key={href}
              href={href}
              className={cn(
                "flex cursor-pointer items-center transition-[background-color,color,transform] duration-200 hover:-translate-y-px active:scale-95",
                "max-md:flex-1 max-md:flex-col max-md:gap-1 max-md:rounded-[21px] max-md:px-0.5 max-md:py-2",
                "md:flex-row md:gap-2 md:rounded-[13px] md:px-4 md:py-[9px]",
                active
                  ? "bg-accent-soft text-accent"
                  : "max-md:text-muted md:text-ink-5",
              )}
            >
              <span className="flex items-center justify-center">
                <Icon />
              </span>
              <span
                className={cn(
                  "max-md:text-[11px] md:text-[13.5px]",
                  active ? "font-semibold" : "font-medium",
                )}
              >
                {label}
              </span>
            </Link>
          );
        })}
    </nav>
  );
}
